import { useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';

import {
  faCheckCircle,
  faChevronDown,
  faChevronUp,
  faSpinner,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans } from '@lingui/react/macro';
import {
  Button,
  Progress,
  Sheet,
  SheetClose,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  ToggleGroup,
  ToggleGroupItem,
} from '@repo/videa-components/src/index';

import type { Appointment } from '@/react-query/useAppointments';
import { useGetZuubInsuranceDetail } from '@/react-query/useGetZuubInsuranceDetail';
import type {
  InsuranceStatusCtaValue,
  StatusTypeValue,
  ZuubBenefitSummaryV4,
  ZuubOrthoBenefitSummary,
  ZuubPatientDetailDataV4,
} from '@/@types/zuub/contracts';
import { useGetDateFNSLocale } from '@/locales/helpers';
import { StatusChip } from '@/common-components/elements/StatusChip/StatusChip';
import { getStatusTypeVariant } from '@/modules/autoVerify/appointmentsList/AutoVerifyTable';
import { isPlanActive } from '@/modules/autoVerify/helpers';
import { getPatientAge } from './patientInfoSheet/PatientInfo';
import routes from '@/routes/routes.constants';
import { formatCurrency } from '@/utils/currency';
import { parseAndFormatDate } from '@/utils/parseDate';

interface BenefitsTabContentProps {
  benefitSummary: ZuubBenefitSummaryV4;
  status: string | null | undefined;
}

const BenefitsTabContent = ({ benefitSummary, status }: BenefitsTabContentProps) => {
  if (!isPlanActive(status ?? null)) {
    return (
      <p className="vai-text-sm vai-text-zinc-500">
        <Trans>No dental benefits data available</Trans>
      </p>
    );
  }
  const { remainingBenefits, usedBenefits, totalBenefits, deductible } =
    benefitSummary;
  const benefitsRemainingPercent =
    totalBenefits > 0 ? (remainingBenefits / totalBenefits) * 100 : 0;

  const deductibleMet =
    deductible != null && deductible.remainingDeductible <= 0;
  const deductibleUsedPercent =
    deductible && deductible.totalDeductible > 0
      ? (deductible.metDeductible / deductible.totalDeductible) * 100
      : 0;

  return (
    <div className="vai-flex vai-flex-col vai-gap-6">
      {/* Benefits */}
      <div className="vai-flex vai-flex-col vai-gap-2">
        <div className="vai-flex vai-justify-between">
          <div>
            <p className="vai-text-sm vai-text-zinc-500">
              <Trans>Benefits Remaining</Trans>
            </p>
            <p className="vai-text-base vai-font-semibold vai-text-zinc-900">
              {formatCurrency(remainingBenefits)}
            </p>
          </div>
          <div className="vai-text-right">
            <p className="vai-text-sm vai-text-zinc-500">
              <Trans>Benefits Used</Trans>
            </p>
            <p className="vai-text-base vai-font-semibold vai-text-zinc-900">
              {formatCurrency(usedBenefits)} / {formatCurrency(totalBenefits)}
            </p>
          </div>
        </div>
        <Progress
          value={benefitsRemainingPercent}
          className="vai-h-2.5 vai-bg-blue-100 vai-rounded-full"
          indicatorClassName="vai-bg-leaf-600 vai-rounded-full"
        />
      </div>

      {/* Deductible */}
      {deductible && (
        <div className="vai-flex vai-flex-col vai-gap-2">
          <div className="vai-flex vai-justify-between">
            <div>
              <p className="vai-text-sm vai-text-zinc-500">
                <Trans>Deductible Remaining</Trans>
              </p>
              <div className="vai-flex vai-items-center vai-gap-2">
                <p className="vai-text-base vai-font-semibold vai-text-zinc-900">
                  {formatCurrency(deductible.remainingDeductible)}
                </p>
                {deductibleMet && (
                  <StatusChip variant="success" text="Deductible Met" />
                )}
              </div>
            </div>
            <div className="vai-text-right">
              <p className="vai-text-sm vai-text-zinc-500">
                <Trans>Deductible Used</Trans>
              </p>
              <div className="vai-flex vai-items-center vai-justify-end vai-gap-1">
                <p className="vai-text-base vai-font-semibold vai-text-zinc-900">
                  {formatCurrency(deductible.metDeductible)} /{' '}
                  {formatCurrency(deductible.totalDeductible)}
                </p>
                {deductibleUsedPercent >= 100 && (
                  <FontAwesomeIcon
                    icon={faCheckCircle}
                    className="vai-text-green-600 vai-text-sm"
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

interface OrthoBenefitsTabContentProps {
  orthoBenefitSummary: ZuubOrthoBenefitSummary;
  status: string | null | undefined;
}

const OrthoBenefitsTabContent = ({
  orthoBenefitSummary,
  status,
}: OrthoBenefitsTabContentProps) => {
  if (!isPlanActive(status ?? null)) {
    return (
      <p className="vai-text-sm vai-text-zinc-500">
        <Trans>No orthodontics benefits data available</Trans>
      </p>
    );
  }
  const { remainingBenefits, usedBenefits, totalBenefits, deductible } =
    orthoBenefitSummary;
  const benefitsRemainingPercent =
    totalBenefits > 0 ? (remainingBenefits / totalBenefits) * 100 : 0;

  const deductibleMet =
    deductible != null && deductible.remainingDeductible <= 0;
  const deductibleUsedPercent =
    deductible && deductible.totalDeductible > 0
      ? (deductible.metDeductible / deductible.totalDeductible) * 100
      : 0;

  return (
    <div className="vai-flex vai-flex-col vai-gap-6">
      <div className="vai-flex vai-flex-col vai-gap-2">
        <div className="vai-flex vai-justify-between">
          <div>
            <p className="vai-text-sm vai-text-zinc-500">
              <Trans>Benefits Remaining</Trans>
            </p>
            <p className="vai-text-base vai-font-semibold vai-text-zinc-900">
              {formatCurrency(remainingBenefits)}
            </p>
          </div>
          <div className="vai-text-right">
            <p className="vai-text-sm vai-text-zinc-500">
              <Trans>Benefits Used</Trans>
            </p>
            <p className="vai-text-base vai-font-semibold vai-text-zinc-900">
              {formatCurrency(usedBenefits)} / {formatCurrency(totalBenefits)}
            </p>
          </div>
        </div>
        <Progress
          value={benefitsRemainingPercent}
          className="vai-h-2.5 vai-bg-blue-100 vai-rounded-full"
          indicatorClassName="vai-bg-leaf-600 vai-rounded-full"
        />
      </div>

      {deductible && (
        <div className="vai-flex vai-flex-col vai-gap-2">
          <div className="vai-flex vai-justify-between">
            <div>
              <p className="vai-text-sm vai-text-zinc-500">
                <Trans>Deductible Remaining</Trans>
              </p>
              <div className="vai-flex vai-items-center vai-gap-2">
                <p className="vai-text-base vai-font-semibold vai-text-zinc-900">
                  {formatCurrency(deductible.remainingDeductible)}
                </p>
                {deductibleMet && (
                  <StatusChip variant="success" text="Deductible Met" />
                )}
              </div>
            </div>
            <div className="vai-text-right">
              <p className="vai-text-sm vai-text-zinc-500">
                <Trans>Deductible Used</Trans>
              </p>
              <div className="vai-flex vai-items-center vai-justify-end vai-gap-1">
                <p className="vai-text-base vai-font-semibold vai-text-zinc-900">
                  {formatCurrency(deductible.metDeductible)} /{' '}
                  {formatCurrency(deductible.totalDeductible)}
                </p>
                {deductibleUsedPercent >= 100 && (
                  <FontAwesomeIcon
                    icon={faCheckCircle}
                    className="vai-text-green-600 vai-text-sm"
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/** Map CTA code to display string (matches AutoVerifyTable for consistent chip text). */
const getInsuranceStatusCtaDisplay = (cta: InsuranceStatusCtaValue | string): string => {
  const normalized = (typeof cta === 'string' ? cta : cta).toUpperCase();
  switch (normalized) {
    case 'ACTIVE':
      return 'Active';
    case 'INACTIVE':
      return 'Inactive';
    case 'CHECK_MANUALLY':
      return 'Check manually';
    case 'CHECK_VIDEA_LOGIN':
      return 'Check Videa log-in';
    case 'CONFIRM_PATIENT_DETAILS':
      return 'Confirm patient details';
    case 'NOT_ELIGIBLE':
      return 'Not Eligible';
    case 'CONFIRM_NPI_NUMBER':
      return 'Confirm NPI number';
    case 'NO_PAYER_LISTED':
      return 'No Payer Listed';
    default:
      return typeof cta === 'string' ? cta : (cta as string);
  }
};

/** Renders coverage rows for a single plan. Uses same StatusChip logic as AutoVerifyTable. */
const CoverageRow = ({ plan, lastRun }: { plan: ZuubPatientDetailDataV4; lastRun: string | null }) => {
  const payerName = plan.payerName ?? plan.plan?.payerName ?? '-';
  const insuranceStatusV2 = plan.insuranceStatusV2;
  const statusType = plan.statusType;
  const fallbackStatus = plan.plan?.status;

  const chipContent = (() => {
    if (!insuranceStatusV2 && !fallbackStatus) {
      return (
        <StatusChip variant="ghost" text="Unknown" />
      );
    }
    const statusForDisplay = insuranceStatusV2 ?? (fallbackStatus as InsuranceStatusCtaValue);
    const variant = statusType
      ? getStatusTypeVariant(statusType as StatusTypeValue)
      : 'default';
    return (
      <StatusChip
        variant={variant}
        text={getInsuranceStatusCtaDisplay(statusForDisplay)}
      />
    );
  })();

  return (
    <div className="vai-grid vai-gap-4 vai-items-center vai-text-sm vai-mb-1 vai-grid-cols-[1fr_1fr_auto]">
      <span className="vai-text-zinc-900 vai-font-medium">{payerName}</span>
      <span className="vai-inline-flex vai-items-center vai-min-w-0">
        {chipContent}
      </span>
      <span className="vai-text-zinc-900 vai-text-right">{lastRun ?? '-'}</span>
    </div>
  );
};

/** Returns dental and ortho benefit summaries for a plan (plan-level or first network). Each plan gets its own; no data = null. */
const getBenefitSummaries = (plan: ZuubPatientDetailDataV4) => ({
  dentalBenefitSummary: plan.benefitSummary ?? plan.networks?.[0]?.benefitSummary ?? null,
  orthoBenefitSummary: plan.orthoBenefitSummary ?? plan.networks?.[0]?.orthoBenefitSummary ?? null,
});

interface InsuranceInfoSheetProps {
  appointment: Appointment;
  pmsPatientId: string | undefined;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onClickPreviousAppt?: () => void;
  onClickNextAppt?: () => void;
}

export const InsuranceInfoSheet = ({
  appointment,
  pmsPatientId,
  open,
  onOpenChange,
  onClickPreviousAppt,
  onClickNextAppt,
}: InsuranceInfoSheetProps) => {
  const history = useHistory();
  const dateFNSLocale = useGetDateFNSLocale();
  const [activeTab, setActiveTab] = useState('dental');
  const [selectedPlanIndex, setSelectedPlanIndex] = useState(0);

  const { data: insuranceDetailData, isLoading } = useGetZuubInsuranceDetail({
    pmsPatientId,
    enabled: open && !!pmsPatientId,
  });

  const patient = appointment.patient;
  const patientName = `${patient.firstName} ${patient.lastName}`;
  const dob = patient.dob ?? '';
  const formattedDob = parseAndFormatDate(dob || undefined, undefined, dateFNSLocale);
  const age = getPatientAge(dob || undefined);

  const sortedPlans = useMemo(() => {
    const plans = insuranceDetailData?.detailedData ?? [];
    return [...plans].sort((a, b) => {
      const aActive = isPlanActive(a.plan?.status ?? null);
      const bActive = isPlanActive(b.plan?.status ?? null);
      if (aActive !== bActive) return aActive ? -1 : 1;
      return 0;
    });
  }, [insuranceDetailData?.detailedData]);

  const lastRunFormatted =
    insuranceDetailData?.lastRun != null
      ? parseAndFormatDate(insuranceDetailData.lastRun, undefined, dateFNSLocale)
      : null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        onOpenAutoFocus={(e) => e.preventDefault()}
        className="vai-max-h-screen vai-flex vai-flex-col sm:vai-max-w-xl"
      >
        <SheetHeader className="vai-flex vai-justify-between vai-bg-gray-100 vai-outline vai-outline-1 vai-outline-slate-300">
          <SheetTitle className="vai-typography-p vai-text-zinc-700 vai-px-4 vai-py-3">
            <Trans>Insurance Information</Trans>
          </SheetTitle>
        </SheetHeader>

        {isLoading ? (
          <div className="vai-flex vai-justify-center vai-items-center vai-h-full">
            <FontAwesomeIcon
              icon={faSpinner}
              className="vai-h-9 vai-w-9 vai-text-zinc-200 vai-animate-spin"
            />
          </div>
        ) : (
          <div className="vai-relative vai-overflow-y-auto vai-flex vai-flex-col vai-flex-grow vai-px-4 vai-pt-4">
            {/* Patient Info */}
            <div className="vai-border-l-4 vai-border-zinc-300 vai-pl-3 vai-mb-6">
              <p className="vai-text-lg vai-font-semibold vai-text-zinc-900">
                {patientName}
              </p>
              <p className="vai-text-sm vai-text-zinc-500">
                {age !== null && (
                  <>
                    <Trans>Age</Trans>: {age} |{' '}
                  </>
                )}
                <Trans>DOB</Trans>: {formattedDob || '-'}
              </p>
            </div>

            {/* Coverage */}
            <div className="vai-mb-6">
              <h3 className="vai-text-base vai-font-semibold vai-text-zinc-900 vai-mb-3">
                <Trans>Coverage</Trans>
              </h3>
              <hr className="vai-border-zinc-200 vai-mb-3" />

              {sortedPlans.length === 0 ? (
                <p className="vai-text-sm vai-text-zinc-500">
                  <Trans>No coverage data available</Trans>
                </p>
              ) : (
                <>
                  <div className="vai-grid vai-gap-4 vai-text-sm vai-text-zinc-500 vai-mb-2 vai-grid-cols-[1fr_1fr_auto]">
                    <span>
                      <Trans>Insurance Payer</Trans>
                    </span>
                    <span className="vai-pl-6">
                      <Trans>Status</Trans>
                    </span>
                    <span className="vai-text-right">
                      <Trans>Last Run</Trans>
                    </span>
                  </div>
                  {sortedPlans.map((plan, idx) => (
                    <CoverageRow
                      key={`${plan.unifiedAppointmentId ?? 'plan'}-${idx}`}
                      plan={plan}
                      lastRun={lastRunFormatted}
                    />
                  ))}
                </>
              )}
            </div>

            {/* Benefits & Deductible */}
            <div>
              <h3 className="vai-text-base vai-font-semibold vai-text-zinc-900 vai-mb-3">
                <Trans>Benefits & Deductible</Trans>
              </h3>
              <hr className="vai-border-zinc-200 vai-mb-3" />

              {sortedPlans.length === 0 ? (
                <p className="vai-text-sm vai-text-zinc-500">
                  <Trans>No benefits data available</Trans>
                </p>
              ) : (
                <>
                  <ToggleGroup
                    type="single"
                    value={String(Math.min(selectedPlanIndex, sortedPlans.length - 1))}
                    onValueChange={(v) => v != null && setSelectedPlanIndex(Number(v))}
                    className="!vai-flex vai-w-full !vai-justify-start vai-flex-wrap vai-gap-2 vai-mb-4"
                  >
                    {sortedPlans.map((plan, idx) => {
                      const payerName = plan.payerName ?? plan.plan?.payerName ?? '-';
                      return (
                        <ToggleGroupItem
                          key={`${plan.unifiedAppointmentId ?? 'plan'}-${idx}`}
                          value={String(idx)}
                          variant="outline"
                          className="vai-rounded-[999px] vai-px-4 vai-py-2 data-[state=on]:vai-bg-blue-100 data-[state=on]:vai-text-blue-800 data-[state=on]:vai-border-blue-800 data-[state=off]:vai-bg-white data-[state=off]:vai-text-zinc-500 data-[state=off]:vai-border-zinc-300"
                        >
                          {payerName}
                        </ToggleGroupItem>
                      );
                    })}
                  </ToggleGroup>
                  <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <TabsList variant="underline" className="vai-w-full vai-mb-4">
                      <TabsTrigger
                        variant="underline"
                        value="dental"
                        className="vai-flex-1"
                      >
                        <Trans>Dental</Trans>
                      </TabsTrigger>
                      <TabsTrigger
                        variant="underline"
                        value="orthodontics"
                        className="vai-flex-1"
                      >
                        <Trans>Orthodontics</Trans>
                      </TabsTrigger>
                    </TabsList>
                    <TabsContent variant="underline" value="dental">
                      {(() => {
                        const idx = Math.min(selectedPlanIndex, sortedPlans.length - 1);
                        const selectedPlan = sortedPlans[idx];
                        const { dentalBenefitSummary } =
                          getBenefitSummaries(selectedPlan);
                        return dentalBenefitSummary ? (
                          <BenefitsTabContent
                            benefitSummary={dentalBenefitSummary}
                            status={selectedPlan.plan?.status ?? null}
                          />
                        ) : (
                          <p className="vai-text-sm vai-text-zinc-500">
                            <Trans>No dental benefits data available</Trans>
                          </p>
                        );
                      })()}
                    </TabsContent>
                    <TabsContent variant="underline" value="orthodontics">
                      {(() => {
                        const idx = Math.min(selectedPlanIndex, sortedPlans.length - 1);
                        const selectedPlan = sortedPlans[idx];
                        const { orthoBenefitSummary } =
                          getBenefitSummaries(selectedPlan);
                        return orthoBenefitSummary ? (
                          <OrthoBenefitsTabContent
                            orthoBenefitSummary={orthoBenefitSummary}
                            status={selectedPlan.plan?.status ?? null}
                          />
                        ) : (
                          <p className="vai-text-sm vai-text-zinc-500">
                            <Trans>No orthodontics benefits data available</Trans>
                          </p>
                        );
                      })()}
                    </TabsContent>
                  </Tabs>
                </>
              )}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="vai-sticky vai-bottom-0 vai-flex vai-justify-end vai-mt-auto vai-bg-white">
          <SheetFooter className="vai-px-4 vai-w-full vai-outline vai-outline-1 vai-outline-slate-300 vai-py-6">
            <div className="vai-flex vai-gap-2">
              {onClickPreviousAppt && (
                <Button
                  className="vai-flex vai-bg-background-accent"
                  variant="ghost"
                  onClick={onClickPreviousAppt}
                >
                  <span className="vai-text-black">
                    <FontAwesomeIcon icon={faChevronUp} />
                  </span>
                </Button>
              )}
              {onClickNextAppt && (
                <Button
                  className="vai-flex vai-bg-background-accent"
                  variant="ghost"
                  onClick={onClickNextAppt}
                >
                  <span className="vai-text-black">
                    <FontAwesomeIcon icon={faChevronDown} />
                  </span>
                </Button>
              )}
            </div>
            <div className="vai-flex vai-gap-2">
              <SheetClose asChild>
                <Button className="vai-cursor-pointer" variant="sheet">
                  <Trans>Close</Trans>
                </Button>
              </SheetClose>
              <Button 
                className="vai-cursor-pointer vai-bg-green-600 hover:vai-bg-green-700 vai-text-white"
                disabled={!pmsPatientId}
                onClick={() => {
                  if (pmsPatientId) {
                    history.push(routes.PATIENT_BENEFITS.replace(':patientId', pmsPatientId));
                  }
                }}
              >
                <Trans>Review Eligibility and Benefits</Trans>
                <span className="vai-ml-1">&rarr;</span>
              </Button>
            </div>
          </SheetFooter>
        </div>
      </SheetContent>
    </Sheet>
  );
};
